import { Modal } from 'antd';
import { PencilLine, Trash2 } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, FormattedPlural, useIntl } from 'react-intl';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  toggleCustomChartModal,
  toggleFullScreenChartModal,
} from '../../../../actions/modalAction';
import { getGroupLabel } from '../../../../js/helper';
import {
  deleteReport,
  isLiteReportsEnabled,
} from '../../../../redux/state-areas/reports/actions';
import {
  makeGetReportQuery,
  makeGetReportTitle,
  makeGetReportTotalTasksCount,
} from '../../../../redux/state-areas/reports/entities/selectors';
import { getWorkspaceCustomFields } from '../../../../redux/workspace/selectors';
import ExportReportButton from '../dashboards/ExportReportButton';
import ReportChartWrapper from '../dashboards/ReportChartWrapper';
import ReportSourceLabel from '../dashboards/ReportSourceLabel';
import { getDrilldownDescriptor } from '../dashboard-widgets/charts/constants';
import { getActiveFilterLabels } from '../dashboard-widgets/utils';
import ConfirmationModal from './ConfirmationModal';

const MODAL_STYLES = {
  body: {
    backgroundColor: '#FFFFFF',
    borderTop: '1px solid #ccc',
    borderBottom: '1px solid #ccc',
    paddingLeft: '0px',
    paddingRight: '0px',
    paddingBottom: '0px',
    height: '70vh',
    overflowY: 'auto',
    overflowX: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    maxWidth: '1250px',
  },
  header: {
    padding: '7px 30px 10px 30px',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  wrapper: {
    overflow: 'hidden',
  },
};

const MODAL_WIDTH = '99vw';
const MODAL_MINWIDTH = { maxWidth: '1250px' };

const FilterText = ({ reportQuery, workspaceCustomFields }) => {
  const activeFilterLabels = useMemo(
    () => getActiveFilterLabels(reportQuery, workspaceCustomFields),
    [reportQuery, workspaceCustomFields],
  );

  if (!activeFilterLabels?.length) return null;

  return (
    <p className="text-xs text-gray-500 mt-0.5">
      <FormattedMessage id="FILTERED_BY" defaultMessage="Filtered by" />{' '}
      {activeFilterLabels
        .map((label) => (
          <FormattedMessage
            key={label}
            id={label?.toUpperCase()?.replace(' ', '_')?.replace('-', '_')}
            defaultMessage={label}
          />
        ))
        .reduce((prev, curr) => [prev, ', ', curr])}
    </p>
  );
};

const mapStateToFilterTextProps = (state) => ({
  workspaceCustomFields: getWorkspaceCustomFields(state),
});
const ConnectedFilterText = connect(mapStateToFilterTextProps)(FilterText);

const ModalTitleNode = ({
  onDelete,
  onEdit,
  reportId,
  reportTitle,
  totalCount,
}) => {
  const intl = useIntl();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingRight: '40px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{reportTitle}</span>
        <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full">
          <FormattedPlural
            value={totalCount}
            other={intl.formatMessage(
              { id: 'OTHER_TASKS', defaultMessage: '{value} Tasks' },
              { value: totalCount || 0 },
            )}
            zero={intl.formatMessage(
              { id: 'NO_TASKS', defaultMessage: 'No Tasks' },
              { value: totalCount || 0 },
            )}
            one={intl.formatMessage(
              { id: 'ONE_TASK', defaultMessage: '{value} Task' },
              { value: totalCount || 0 },
            )}
            two={intl.formatMessage(
              { id: 'TWO_TASKS', defaultMessage: '{value} Tasks' },
              { value: totalCount || 0 },
            )}
            few={intl.formatMessage(
              { id: 'FEW_TASKS', defaultMessage: '{value} Tasks' },
              { value: totalCount || 0 },
            )}
            many={intl.formatMessage(
              { id: 'MANY_TASKS', defaultMessage: '{value} Tasks' },
              { value: totalCount || 0 },
            )}
          />
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        <ExportReportButton reportId={reportId} reportTitle={reportTitle} />
        <button
          onClick={onEdit}
          className="p-2 hover:bg-indigo-50 rounded-lg transition-colors group"
          title={intl.formatMessage({
            id: 'EDIT_CHART',
            defaultMessage: 'Edit chart',
          })}
        >
          <PencilLine
            size={16}
            className="text-gray-400 group-hover:text-indigo-600 transition-colors"
          />
        </button>
        <button
          onClick={onDelete}
          className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
          title={intl.formatMessage({
            id: 'DELETE_CHART',
            defaultMessage: 'Delete chart',
          })}
        >
          <Trash2
            size={16}
            className="text-gray-400 group-hover:text-red-600 transition-colors"
          />
        </button>
      </div>
    </div>
  );
};

const ChartFullScreenModal = ({
  dashboardId,
  isModalOpen,
  reportId,
  reportQuery,
  title,
  totalCount,
  workspaceCustomFields,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [reportIdToDelete, setReportIdToDelete] = useState(null);

  const closeDeleteReportConfirmation = useCallback(
    () => setReportIdToDelete(null),
    [],
  );

  const handleDeleteReport = useCallback(
    () => {
      closeDeleteReportConfirmation();
      closeModal();
      dispatch(deleteReport(intl, reportIdToDelete, dashboardId));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closeDeleteReportConfirmation, reportIdToDelete, dashboardId],
  );

  const reportTitle = useMemo(
    () => getGroupLabel(title, workspaceCustomFields, true),
    [title, workspaceCustomFields],
  );

  const closeModal = useCallback(() => {
    dispatch(toggleFullScreenChartModal(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Same two navigation shapes as ReportContainer: `taskIds` enumerates the
  // task set, `drilldown` describes it and lets the target page resolve it
  // through the drill-down endpoint. Kept deliberately identical so the widget
  // and its full-screen view can never disagree about what a click opens.
  const navigateToChartTasks = useCallback(
    (taskIds, category) => {
      closeModal();
      navigate(`/dashboard/${dashboardId}/chart-tasks`, {
        state: { taskIds, category, reportId, reportTitle },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dashboardId, reportId, reportTitle, navigate],
  );

  const navigateToDrilldown = useCallback(
    (drilldown, category) => {
      closeModal();
      navigate(`/dashboard/${dashboardId}/chart-tasks`, {
        state: { drilldown, category, reportId, reportTitle },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dashboardId, reportId, reportTitle, navigate],
  );

  const handleSliceClick = useCallback(
    (data, chartData, seriesKey) => {
      const sliceName = data?.name;
      const row = chartData?.find((v) => v?.name === sliceName);
      const taskIds = row?.taskIds;
      // A stacked/grouped-bar segment click (seriesKey present) must always
      // resolve through the descriptor when the flag is on — see the matching
      // comment in ReportContainer.jsx's handleSliceClick (Codex F-001): the
      // row's embedded `taskIds` only ever holds the FIRST stack's ids for a
      // merged group row, and can be non-empty even in lite mode (e.g. right
      // after `updateReport`, which always returns the full shape).
      const isStackedSegmentClick = seriesKey !== undefined;
      // Embedded ids otherwise win, so a full response (flag off, or the
      // server kill switch overriding it) keeps behaving exactly as before.
      if (!isLiteReportsEnabled() || (!isStackedSegmentClick && taskIds?.length)) {
        if (!taskIds) return;
        navigateToChartTasks(taskIds, sliceName);
        return;
      }
      const drilldown = getDrilldownDescriptor(row, seriesKey);
      // No descriptor ⇒ nothing identifies this slice; navigating with an empty
      // one would silently show every task instead. Do nothing, as an id-less
      // row does today.
      if (!drilldown) return;
      navigateToDrilldown(drilldown, sliceName);
    },
    [navigateToChartTasks, navigateToDrilldown],
  );

  const handleDelete = useCallback(() => {
    setReportIdToDelete?.(reportId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  const handleEdit = useCallback(() => {
    dispatch(
      toggleCustomChartModal(true, dashboardId, 'updatedTitle', reportId),
    );
    closeModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, dashboardId]);

  return (
    <>
      <Modal
        centered
        title={
          <ModalTitleNode
            onDelete={handleDelete}
            onEdit={handleEdit}
            reportId={reportId}
            reportTitle={reportTitle}
            totalCount={totalCount}
          />
        }
        open={isModalOpen}
        onCancel={closeModal}
        styles={MODAL_STYLES}
        style={MODAL_MINWIDTH}
        className="custom-modal"
        footer={null}
        width={MODAL_WIDTH}
        destroyOnClose
        zIndex={900}
      >
        <div className="px-6 pt-4 pb-3 flex-1">
          <p className="text-xs text-gray-500 mt-0.5 flex gap-1">
            <FormattedMessage
              id="INCLUDE_TASKS_FROM"
              defaultMessage="Include tasks from"
            />
            {':'}
            <ReportSourceLabel reportId={reportId} />
          </p>
          <ConnectedFilterText reportQuery={reportQuery} />
        </div>
        <div
          className="px-1 min-h-[400px] min-w-[920px] flex flex-col flex-1"
        >
          <div className="flex-1 min-h-0">
            <ReportChartWrapper
              reportId={reportId}
              handleSliceClick={handleSliceClick}
              maxHeight="400px"
              isFullscreen
            />
          </div>
          <div className="h-[20px] w-full flex-none" />
        </div>
      </Modal>
      <ConfirmationModal
        title={intl.formatMessage({
          id: 'DELETE_CHART',
          defaultMessage: 'Delete Chart?',
        })}
        description={intl.formatMessage({
          id: 'DELETE_CHART_CONFIRMATION',
          defaultMessage: 'Are you sure you want to delete chart?',
        })}
        isModalOpen={reportIdToDelete}
        onCancel={closeDeleteReportConfirmation}
        onYesClick={handleDeleteReport}
      />
    </>
  );
};

const makeMapStateToProps = () => {
  const getReportQuery = makeGetReportQuery();
  const getReportTitle = makeGetReportTitle();
  const getTotalCount = makeGetReportTotalTasksCount();

  return (state) => {
    const modalData = state.modalReducer.chartFullScreenModal;
    const reportId = modalData?.reportId;
    const dashboardId = modalData?.dashboardId;
    const isModalOpen = modalData?.show ?? false;

    return {
      dashboardId,
      isModalOpen,
      reportId,
      reportQuery: getReportQuery(state, { reportId }),
      title: getReportTitle(state, { reportId }),
      totalCount: getTotalCount(state, { reportId }),
      workspaceCustomFields: getWorkspaceCustomFields(state),
    };
  };
};

export default connect(makeMapStateToProps)(ChartFullScreenModal);
